import { useRef, type ReactNode } from 'react';
import type { AssetDto } from '@indusketch/shared';
import { formatNumber } from '@indusketch/project-model';
import { Icon } from '../../ui/Icon.js';
import { MenuButton, type MenuItem } from '../../ui/Menu.js';
import { useI18n } from '../../i18n/i18n.js';
import { useAssetThumbnail, useSeenOnScreen } from './thumbnails.js';

export const formatBytes = (b: number) =>
  b > 1024 * 1024 ? `${formatNumber(b / 1024 / 1024, 1)} MB` : `${formatNumber(b / 1024, 1)} KB`;

type CardAsset = Pick<AssetDto, 'id' | 'name' | 'sizeBytes' | 'createdAt' | 'metadata'>;

/** Tile of an uploaded 3D model: rendered preview, real-world size and file facts. */
export function AssetCard({
  asset: a,
  actions,
  badge,
  footer,
}: {
  asset: CardAsset;
  actions: readonly MenuItem[];
  /** Small label on the preview, e.g. "System". */
  badge?: string;
  /** Extra line below the facts, e.g. the owner in the admin area. */
  footer?: ReactNode;
}) {
  const { m, fmt, plural } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const url = useAssetThumbnail(a.id, a.createdAt, useSeenOnScreen(ref));
  const s = a.metadata.size;
  const name = a.name.replace(/\.glb$/i, '');
  const draco = a.metadata.extensions.includes('KHR_draco_mesh_compression');
  return (
    <article className="tile asset-tile" data-testid={`asset-card-${a.id}`}>
      <div className="tile-preview asset-preview" ref={ref}>
        {url ? <img src={url} alt="" /> : <Icon name="box" size={36} className="tile-placeholder" />}
        {badge && <span className="tile-badge">{badge}</span>}
      </div>
      <div className="tile-body">
        <div className="tile-title-row">
          <strong className="tile-title" title={a.name}>
            {name}
          </strong>
          <MenuButton label={fmt(m.assets.actions, { name })} items={actions} />
        </div>
        <span className="tile-fact" title={m.assets.dimensions}>
          {[s.width, s.depth, s.height].map((v) => formatNumber(v, 2)).join(' × ')} m
        </span>
        <span className="muted tile-meta">
          {formatBytes(a.sizeBytes)} ·{' '}
          {plural(m.assets.triangles, a.metadata.triangles, {
            n: formatNumber(a.metadata.triangles, 0),
          })}
          {draco ? ' · Draco' : ''}
        </span>
        {footer}
      </div>
    </article>
  );
}
